import React, { useEffect } from "react";
import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { LoginPage } from "@/pages/AuthPages";
import { GuestDashboard } from "@/pages/GuestDashboard";
import { AdminDashboard } from "@/pages/AdminDashboard";
import { ChangePasswordModal } from "@/components/ChangePasswordModal";
import InstallPrompt from "@/components/InstallPrompt";
import VerifyRedeemPage from "@/pages/VerifyRedeemPage";
import { PrivacyPage, DeleteAccountPage, TermsPage } from "@/pages/LegalPagesTranslated";
import { pushService } from "@/services/pushService";
import { useLanguage } from "@/contexts/LanguageContext";
import LoadingIndicator from "@/components/LoadingIndicator";

function MainApp() {
  const { user, loading, checkAuth } = useAuth();
  const { language } = useLanguage();

  useEffect(() => {
    if (process.env.NODE_ENV === 'production' && pushService.isSupported()) {
      pushService.registerServiceWorker();
    }
  }, []);

  useEffect(() => {
    const isStandalone =
      window.matchMedia?.('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    if (!isStandalone) return;

    try { window.history.pushState({ __wanna_back_guard: true }, ''); } catch (e) {}

    const onPop = (e) => {
      try { window.history.pushState({ __wanna_back_guard: true }, ''); } catch (err) {}
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  if (loading) {
    return <LoadingIndicator fullScreen />;
  }

  if (!user) return (
    <>
      <LoginPage />
      <InstallPrompt language={language} />
    </>
  );

  if (user.must_change_password) {
    return (
      <div className="min-h-screen-safe bg-background flex items-center justify-center p-4">
        <ChangePasswordModal onSuccess={() => checkAuth()} />
      </div>
    );
  }

  if (user.role === 'admin' || user.role === 'house_manager') {
    return (
      <>
        <AdminDashboard />
        <InstallPrompt language={language} />
      </>
    );
  }

  return (
    <>
      <GuestDashboard />
      <InstallPrompt language={language} />
    </>
  );
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Uncaught error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6 text-center">
          <div className="border-2 border-foreground rounded-xl p-6 max-w-md w-full bg-surface shadow-brutal">
            <h2 className="font-heading font-bold text-xl mb-2 text-foreground">Ocurrió un error inesperado</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Se produjo un problema al cargar la vista. Por favor, intentá recargar la página.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="w-full py-3 border-2 border-foreground rounded-lg bg-accent-yellow font-bold text-sm hover:opacity-90 transition-opacity"
            >
              Recargar página
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <LanguageProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/verify/:token" element={<VerifyRedeemPage />} />
              <Route path="/privacidad" element={<PrivacyPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/eliminar-cuenta" element={<DeleteAccountPage />} />
              <Route path="/delete-account" element={<DeleteAccountPage />} />
              <Route path="/terminos" element={<TermsPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/*" element={<AuthProvider><MainApp /></AuthProvider>} />
            </Routes>
          </BrowserRouter>
        </LanguageProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
